import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { showBanner } from '../../global-state/banner-slice'
import { blogServices, BlogFormData } from '../../services/blog-service'
import { createSlug, isValidImageType } from '../../lib/utils/helpers'
import { BlogsFormPresenter } from './blogs-form-presenter'

const MAX_FILE_SIZE_MB = 10

const defaultValues: BlogFormData = {
  title: '',
  sub_title: '',
  slug: '',
  content: '',
  banner_image: '',
  status: 'DRAFT',
  category: 'MARKET_NEWS',
  linkedin_url: '',
  reading_time_minutes: undefined,
}

function BlogsFormContainer() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = Boolean(id)

  const blogRef = useRef<BlogFormData | null>(null)

  const form = useForm<BlogFormData>({
    defaultValues,
    mode: 'onBlur',
  })

  const [bannerPreview, setBannerPreview] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!id) return
    const fetchBlog = async () => {
      try {
        setIsLoading(true)
        const res = await blogServices.getBlogById(id)
        if (res?.data) {
          const blog = res.data
          blogRef.current = blog
          form.reset({
            title: blog.title ?? defaultValues.title,
            sub_title: blog.sub_title ?? defaultValues.sub_title,
            slug: blog.slug ?? defaultValues.slug,
            content: blog.content ?? defaultValues.content,
            banner_image: blog.banner_image ?? defaultValues.banner_image,
            status: blog.status ?? defaultValues.status,
            category: blog.category ?? defaultValues.category,
            linkedin_url: blog.linkedin_url ?? defaultValues.linkedin_url,
            reading_time_minutes: blog.reading_time_minutes ?? defaultValues.reading_time_minutes,
          })
          if (blog.banner_image) setBannerPreview(blog.banner_image)
        }
      } catch (error) {
        console.error(error)
        dispatch(showBanner({ text: 'Failed to fetch blog', variant: 'error-banner' }))
      } finally {
        setIsLoading(false)
      }
    }
    fetchBlog()
  }, [id])

  const handleTitleBlur = () => {
    const title = form.getValues('title')
    const currentSlug = form.getValues('slug')
    if (title && !currentSlug) {
      form.setValue('slug', createSlug(title), { shouldValidate: true })
    }
  }

  const handleFileUpload = async (file: File) => {
    if (!isValidImageType(file)) {
      dispatch(showBanner({ text: 'Only JPEG/JPG images are supported', variant: 'error-banner' }))
      return
    }
    const sizeInKB = Math.round(file.size / 1024)
    if (sizeInKB > MAX_FILE_SIZE_MB * 1024) {
      dispatch(
        showBanner({
          text: `File size must be under ${MAX_FILE_SIZE_MB} MB`,
          variant: 'error-banner',
        }),
      )
      return
    }

    const res: any = await blogServices.uploadFile(file)
    if (res?.data?.key) {
      form.setValue('banner_image', res.data.key, { shouldValidate: true, shouldDirty: true })
      setBannerPreview(URL.createObjectURL(file))
    } else {
      dispatch(showBanner({ text: 'Failed to upload image', variant: 'error-banner' }))
    }
  }

  const handleRemoveBanner = () => {
    form.setValue('banner_image', '', { shouldValidate: true, shouldDirty: true })
    setBannerPreview('')
  }

  const onSubmit = async (data: BlogFormData) => {
    setIsSubmitting(true)
    try {
      if (isEditMode && id) {
        if (blogRef.current) {
          Object.entries(data).forEach(([key, value]) => {
            if (
              value === '' ||
              value === null ||
              value === undefined ||
              value === blogRef.current?.[key as keyof BlogFormData]
            ) {
              delete data[key as keyof BlogFormData]
            }
          })
        }
        if (Object.keys(data).length === 0) {
          dispatch(showBanner({ text: 'No changes to update', variant: 'warning-banner' }))
          return
        }
        const res = await blogServices.updateBlog(id, data)

        if (res?.status === 200) {
          dispatch(showBanner({ text: 'Blog updated successfully', variant: 'success-banner' }))
          navigate('/blogs')
        } else {
          dispatch(
            showBanner({
              text: res?.data?.message || 'Something went wrong! Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      } else {
        const res = await blogServices.createBlog(data)
        if (res?.status === 200 || res?.status === 201) {
          dispatch(showBanner({ text: 'Blog created successfully', variant: 'success-banner' }))
          navigate('/blogs')
        } else {
          dispatch(
            showBanner({
              text: res?.data?.message || 'Something went wrong! Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <BlogsFormPresenter
      form={form}
      isEditMode={isEditMode}
      isSubmitting={isSubmitting}
      isLoading={isLoading}
      bannerPreview={bannerPreview}
      onSubmit={onSubmit}
      onTitleBlur={handleTitleBlur}
      onFileUpload={handleFileUpload}
      onRemoveBanner={handleRemoveBanner}
    />
  )
}

export { BlogsFormContainer }
